import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
} from '@/components/ui/OptimizedAnimations';
import { CharacterStagger } from '@/components/ui/AceternityAnimations';

const sections = [
  {
    icon: Database,
    title: 'Information We Collect',
    content: `We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include:

• Name and contact information (email, phone number, address)
• Institution details (school/college name, designation)
• Payment information (processed securely through our payment partners)
• Usage data and preferences
• Communication records with our support team`,
  },
  {
    icon: Eye,
    title: 'How We Use Your Information',
    content: `We use the information we collect to:

• Provide, maintain, and improve our services
• Process transactions and send related information
• Send technical notices, updates, and support messages
• Respond to your comments, questions, and customer service requests
• Monitor and analyze trends, usage, and activities
• Personalize and improve your experience
• Comply with legal obligations`,
  },
  {
    icon: Lock,
    title: 'Data Security',
    content: `We implement industry-standard security measures to protect your data:

• AES-256 encryption for data at rest and in transit
• ISO 27001 compliant security practices
• Regular security audits and penetration testing
• Secure data centers with physical access controls
• Role-based access control for our team members
• Daily encrypted backups with 30-day retention`,
  },
  {
    icon: UserCheck,
    title: 'Your Rights',
    content: `You have the right to:

• Access the personal data we hold about you
• Request correction of inaccurate data
• Request deletion of your data (subject to legal requirements)
• Object to processing of your data
• Request data portability
• Withdraw consent at any time

To exercise these rights, contact us at privacy@tattvatechworkz.com`,
  },
  {
    icon: Shield,
    title: 'Data Retention',
    content: `We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including:

• Active account data: Retained while your account is active
• Transaction records: 7 years (as required by law)
• Support communications: 3 years after resolution
• Analytics data: Aggregated and anonymized after 2 years

Upon account deletion, we remove personal data within 30 days, except where retention is required by law.`,
  },
];

const PrivacyPage = () => {
  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient opacity-50" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal delay={0.1}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-teal text-sm mb-8 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </ScrollReveal>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-teal/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-teal" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                Privacy Policy
              </h1>
              <p className="text-muted-foreground text-sm">
                Last updated: July 2026
              </p>
            </div>
          </div>

          <ScrollReveal delay={0.3}>
            <p className="text-muted-foreground text-lg mb-8">
              At Tattva Techworkz, we are committed to protecting your privacy and ensuring
              the security of your personal information. This policy explains how we collect,
              use, and safeguard your data.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer staggerDelay={0.1} className="space-y-8">
            {sections.map((section, index) => (
              <StaggerItem key={section.title}>
                <motion.div
                  whileHover={{ y: -2 }}
                  className="glass-card p-8"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-teal/20 flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-5 h-5 text-teal" />
                    </div>
                    <h2 className="text-xl font-display font-semibold text-foreground">
                      {section.title}
                    </h2>
                  </div>
                  <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line pl-14">
                    {section.content}
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h3 className="text-display text-2xl text-foreground mb-4">
              Questions About Privacy?
            </h3>
            <p className="text-muted-foreground mb-6">
              If you have any questions about this Privacy Policy, please contact our Data Protection Officer.
            </p>
            <div className="inline-flex items-center gap-3 glass-card px-6 py-4">
              <Mail className="w-5 h-5 text-teal" />
              <a
                href="mailto:privacy@tattvatechworkz.com"
                className="text-teal hover:text-teal-light transition-colors"
              >
                privacy@tattvatechworkz.com
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
